import type { EventDropArg, EventInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, {
  Draggable,
  type EventReceiveArg,
  type EventResizeDoneArg,
} from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Button, Dialog, Input } from '@/components/ui';
import { api } from '@/lib/api';
import { useDetail, useStructure, useUpdateNode } from '@/lib/hooks';

const RITUAL_LABEL = { daily_prep: 'Daily prep', weekly_review: 'Weekly review' } as const;
const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};
const dateOnly = (d: Date) => new Intl.DateTimeFormat('en-CA').format(d);

export function CalendarPage() {
  const { data: nodes } = useStructure();
  const { data: rituals } = useQuery({ queryKey: ['upcoming', 60], queryFn: () => api.upcoming(60) });
  const update = useUpdateNode();
  const { open } = useDetail();
  const moveRitual = useMutation({
    mutationFn: (v: { id: number; at: string | null }) => api.updateSchedule(v.id, { next_run_at: v.at }),
  });
  const [ritual, setRitual] = useState<{ id: number; type: string; at: string } | null>(null);
  const [ritualAt, setRitualAt] = useState('');
  const externalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!externalRef.current) return;
    const d = new Draggable(externalRef.current, {
      itemSelector: '[data-node-id]',
      eventData: (el) => ({ title: el.getAttribute('data-title') ?? '', duration: '00:30', create: false }),
    });
    return () => d.destroy();
  }, []);

  const events: EventInput[] = [];
  for (const n of nodes ?? []) {
    if (n.scheduled_at) {
      const start = new Date(n.scheduled_at);
      events.push({
        id: `s-${n.id}`,
        title: n.title,
        start,
        end: new Date(start.getTime() + (n.duration_min ?? 30) * 60_000),
        extendedProps: { kind: 'scheduled', nodeId: n.id },
        color: n.assignee_type === 'agent' ? '#7c3aed' : '#5b4fe0',
      });
    }
    if (n.due_date) {
      events.push({
        id: `d-${n.id}`,
        title: `Due: ${n.title}`,
        start: n.due_date,
        allDay: true,
        extendedProps: { kind: 'due', nodeId: n.id },
        color: '#d97706',
      });
    }
  }
  for (const r of rituals ?? []) {
    events.push({
      id: `r-${r.schedule_id}-${r.at}`,
      title: RITUAL_LABEL[r.type] + (r.overridden ? ' (moved)' : ''),
      start: r.at,
      end: new Date(new Date(r.at).getTime() + 30 * 60_000),
      editable: false,
      extendedProps: { kind: 'ritual', scheduleId: r.schedule_id, type: r.type, at: r.at },
      color: '#16a34a',
    });
  }

  const unscheduled = (nodes ?? []).filter((n) => n.type === 'task' && n.is_next_action && !n.scheduled_at);

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border bg-surface/60 px-4 py-3 md:px-6">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold leading-tight">Calendar</h1>
          <p className="hidden text-xs text-muted-fg sm:block">
            Drag tasks to schedule them. Click a ritual to move that date.
          </p>
        </div>
        {unscheduled.length > 0 && (
          <div className="flex w-full min-w-0 items-center gap-2">
            <span className="shrink-0 text-xs font-medium text-muted-fg">Unscheduled</span>
            <div ref={externalRef} className="flex min-w-0 gap-1 overflow-x-auto pb-0.5">
              {unscheduled.map((n) => (
                <div
                  key={n.id}
                  data-node-id={n.id}
                  data-title={n.title}
                  className="max-w-56 shrink-0 cursor-grab truncate rounded-lg border border-border bg-muted px-2.5 py-1.5 text-xs font-medium hover:border-primary"
                >
                  {n.title}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="min-h-0 flex-1 bg-surface p-3 md:px-4">
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay',
          }}
          firstDay={1}
          locale="en-gb"
          nowIndicator
          editable
          droppable
          height="100%"
          slotMinTime="06:00:00"
          slotMaxTime="23:00:00"
          events={events}
          eventClick={(info) => {
            const p = info.event.extendedProps;
            if (p.kind === 'ritual') {
              setRitual({ id: p.scheduleId, type: p.type, at: p.at });
              setRitualAt(toLocalInput(p.at));
            } else open(p.nodeId);
          }}
          eventDrop={(info: EventDropArg) => {
            const p = info.event.extendedProps;
            if (p.kind === 'scheduled')
              update.mutate({ id: p.nodeId, scheduled_at: info.event.start!.toISOString() });
            else if (p.kind === 'due') update.mutate({ id: p.nodeId, due_date: dateOnly(info.event.start!) });
            else info.revert();
          }}
          eventResize={(info: EventResizeDoneArg) => {
            const p = info.event.extendedProps;
            if (p.kind !== 'scheduled' || !info.event.end) return info.revert();
            update.mutate({
              id: p.nodeId,
              duration_min: Math.round((info.event.end.getTime() - info.event.start!.getTime()) / 60_000),
            });
          }}
          eventReceive={(info: EventReceiveArg) => {
            const id = Number(info.draggedEl.getAttribute('data-node-id'));
            const start = info.event.start!;
            info.event.remove();
            update.mutate({
              id,
              scheduled_at: start.toISOString(),
              duration_min: info.event.allDay ? null : 30,
            });
          }}
        />
      </div>
      <Dialog
        open={!!ritual}
        onOpenChange={(o) => !o && setRitual(null)}
        title={ritual ? `Move ${RITUAL_LABEL[ritual.type as keyof typeof RITUAL_LABEL]}` : ''}
      >
        <p className="mb-2 text-sm text-muted-fg">
          Moves the next occurrence only. The regular schedule stays as it is (change it in Settings).
        </p>
        <Input type="datetime-local" value={ritualAt} onChange={(e) => setRitualAt(e.target.value)} />
        <div className="mt-3 flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() =>
              ritual && moveRitual.mutate({ id: ritual.id, at: null }, { onSuccess: () => setRitual(null) })
            }
          >
            Reset to schedule
          </Button>
          <Button
            onClick={() =>
              ritual &&
              moveRitual.mutate(
                { id: ritual.id, at: new Date(ritualAt).toISOString() },
                { onSuccess: () => setRitual(null) },
              )
            }
          >
            Move
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
